import { Link } from 'react-router-dom';
import {
  ArrowUpRight,
  ArrowRight,
  QrCode,
  MessageCircle,
  Radio,
  Mic,
  EyeOff,
  Smartphone,
  Wifi,
  Sparkles,
  ShieldCheck,
  Plug,
} from 'lucide-react';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@/components/ui/accordion';
import PageShell from '@/components/PageShell';
import SectionHeader from '@/components/SectionHeader';

// ---------- Data ----------
const CHALLENGES = [
  {
    n: '01',
    t: 'Dealership personnel as gatekeepers',
    d: 'Every customer signal — interest, objection, complaint — passes through someone whose incentives are not perfectly aligned with the OEM\'s. What gets typed in is rarely what got said.',
  },
  {
    n: '02',
    t: '750 dealerships, ~750 versions of "the truth"',
    d: 'Different DMS, different CRMs, different handwritten registers. Even a basic question — "how many walk-ins yesterday?" — has 750 confidence intervals.',
  },
  {
    n: '03',
    t: 'Funnel leakage is invisible',
    d: 'Walk-in → test ride → quote → booking → delivery → first service → second service. Each step loses data. The brand sees deliveries; everything before that is a fog.',
  },
  {
    n: '04',
    t: 'Sales staff don\'t want to type',
    d: 'A 22-year-old salesperson in a Tier-3 town will not log a 14-field CRM entry for every walk-in. Heavy software gets selectively used at best, ignored at worst.',
  },
  {
    n: '05',
    t: 'Feedback arrives late, scrubbed, and aggregated',
    d: 'NPS comes weeks after delivery, often "helped" by the dealer. Real moments-of-truth (a stalled test ride, a delayed delivery, a cold service handover) are never captured.',
  },
  {
    n: '06',
    t: 'Mobile-first customer, paper-first dealer',
    d: 'The buyer is on Instagram and WhatsApp. The dealership is on PDFs and printouts. The brand promise breaks the moment the customer walks in.',
  },
  {
    n: '07',
    t: 'No real-time dealer scorecard',
    d: 'Region heads find out a dealership has gone cold a month too late. By the time the meeting happens, three buyers have already gone to the competitor next door.',
  },
  {
    n: '08',
    t: 'Service is a second brand',
    d: 'Customers fall in love at sale, fall out at first service. The OEM has almost no real-time view into the workshop bay where the relationship is actually made or broken.',
  },
];

const SOLUTIONS = [
  {
    icon: QrCode,
    n: '01',
    t: 'QR-on-vehicle · 10-second journey start',
    d: 'Every test-ride bike carries a sticker QR. One scan starts a WhatsApp journey on the customer\'s phone — no app, no install, no dealer typing. Geo, model, time, customer number captured in seconds.',
  },
  {
    icon: MessageCircle,
    n: '02',
    t: 'WhatsApp-native journey · works on 2G',
    d: 'India\'s most-used app becomes the OEM\'s direct-line. Micro-surveys triggered at every moment-of-truth — booking, test ride, delivery, first ride, first service — in the customer\'s language, in 30 seconds.',
  },
  {
    icon: Radio,
    n: '03',
    t: 'Shadow funnel · dealer-blind capture',
    d: 'Ambient capture at the dealership — anonymised footfall, dwell time, test-ride starts — measured against bookings and deliveries. The OEM can see the funnel the dealer can\'t edit.',
  },
  {
    icon: Mic,
    n: '04',
    t: 'Voice-AI on service-advisor calls',
    d: 'Indic-language voice models listen to recorded calls and walk-in conversations (with consent) to extract intent, objections, sentiment and churn risk — without the advisor writing a single note.',
  },
  {
    icon: EyeOff,
    n: '05',
    t: '"Secret rider" mystery customer program',
    d: 'A gamified, verified network of real customers does paid micro-visits — score sales pitch quality, financing transparency, delivery experience. Independent, scalable, statistically meaningful.',
  },
  {
    icon: Smartphone,
    n: '06',
    t: 'PWA for dealer staff · < 500 KB · offline-first',
    d: 'A progressive web app that runs in the browser on any Android phone. No install, no version chaos, works offline, syncs when 4G returns. Replaces five forms with three taps.',
  },
  {
    icon: Plug,
    n: '07',
    t: 'Vehicle-telemetry hook for connected EVs',
    d: 'For connected models, first-ride data, charging habits, and ride patterns flow back automatically. The brand learns who actually loves the bike — and who quietly stops riding — before NPS ever asks.',
  },
  {
    icon: Sparkles,
    n: '08',
    t: 'AI mystery shopper · automated dealer audits',
    d: 'AI-generated phone enquiries to dealerships score response time, pitch quality, finance disclosure, and follow-up discipline — at scale, every week, across all 750 dealerships.',
  },
  {
    icon: Wifi,
    n: '09',
    t: 'DMS-agnostic adapters',
    d: 'We don\'t fight your existing systems — Salesforce, Zoho, in-house, paper. Adapters pull what exists, fill the gaps via WhatsApp + QR, deliver one unified customer record to the OEM.',
  },
  {
    icon: ShieldCheck,
    n: '10',
    t: 'DPDP-compliant by design',
    d: 'Consent at every capture, India-resident data, purpose-bound usage, retention controls. Compliance baked in — not bolted on.',
  },
];

const FAQS = [
  {
    q: 'We already have a CRM and a DMS. Why add another layer?',
    a: 'You don\'t. The platform sits above what you already use — pulls from CRM/DMS via adapters, fills only the gaps the dealer cannot or will not fill, and gives the OEM a single customer truth. It is meant to be light on the dealership, not heavy.',
  },
  {
    q: 'Will dealers push back?',
    a: 'Some will, briefly. Because almost all customer capture happens on the customer\'s phone (QR + WhatsApp), and most surveys bypass the showroom staff, there is nothing operationally heavier for the dealer to do — and in fact, the dealer benefits from better leads and clearer service scorecards.',
  },
  {
    q: 'How long to go live across 750 dealerships?',
    a: 'A pilot of ~25 dealerships in 4–6 weeks. National rollout across 750 dealerships typically in 3–4 months, sequenced by region and DMS type.',
  },
  {
    q: 'Will this work in Tier-3 / Tier-4 towns with poor connectivity?',
    a: 'Yes. WhatsApp works on 2G. The dealer PWA is offline-first and under 500 KB. The QR-based test-ride journey works even when the dealership Wi-Fi is down.',
  },
  {
    q: 'What about customer privacy and DPDP?',
    a: 'Consent is captured at the first WhatsApp message. All data stays in India, with purpose-bound usage, configurable retention, and audit trails. We treat DPDP as table-stakes, not an afterthought.',
  },
  {
    q: 'Can you integrate with our finance & insurance partners?',
    a: 'Yes — most OEMs have 8–15 finance and 4–6 insurance partners. We integrate via APIs where available and via WhatsApp / OTP flows where not, so the customer never has to re-enter data on the showroom floor.',
  },
  {
    q: 'What does this cost a dealership?',
    a: 'Nothing in capex. There is no hardware to install, no system to license at the dealership. The platform is licensed at the OEM level — the dealer experience is just a small PWA and a few WhatsApp threads.',
  },
  {
    q: 'How is this different from a standard CRM or NPS survey?',
    a: 'CRMs capture what the dealer types. NPS surveys ask the customer weeks later. This platform captures the customer journey directly, in real time, at every moment of truth — bypassing dealer bias and survey fatigue.',
  },
  {
    q: 'Can we white-label it inside our own brand app?',
    a: 'Yes. The WhatsApp journeys, surveys and dashboards can be entirely OEM-branded, including in your own consumer app if you have one.',
  },
  {
    q: 'Does this work only for EV 2-wheelers or any OEM?',
    a: 'Any 2-wheeler OEM — ICE, EV, premium, mass-market. The same playbook also adapts well for PV and CV OEMs with dealer networks of similar scale.',
  },
];

// ---------- Page ----------
const INSIGHT_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: '750 dealerships. One customer truth. — A 2-wheeler OEM intelligence blueprint',
  description:
    'A working blueprint for any 2-wheeler OEM serious about owning the customer relationship at industrial scale — across 750+ dealerships, without depending on the dealership. QR + WhatsApp + voice-AI + DMS-agnostic adapters, DPDP-compliant by design.',
  url: 'https://themobilitygroup.co/insights/two-wheeler-dealerships',
  image: 'https://themobilitygroup.co/og-image.png',
  inLanguage: 'en',
  isAccessibleForFree: true,
  articleSection: 'Insight · 2-Wheeler OEM intelligence',
  keywords: [
    '2-wheeler OEM',
    'dealer network intelligence',
    'customer journey',
    'EV 2W',
    'WhatsApp commerce',
    'Indic voice AI',
    'DPDP compliance',
    'dealer SLA',
  ],
  author: {
    '@type': 'Organization',
    '@id': 'https://themobilitygroup.co/#organization',
    name: 'The Mobility Group',
  },
  publisher: {
    '@type': 'Organization',
    '@id': 'https://themobilitygroup.co/#organization',
    name: 'The Mobility Group',
    logo: {
      '@type': 'ImageObject',
      url: 'https://themobilitygroup.co/og-image.png',
    },
  },
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': 'https://themobilitygroup.co/insights/two-wheeler-dealerships',
  },
};

const Insight2W = () => {
  return (
    <PageShell>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(INSIGHT_JSONLD) }}
      />
      {/* HERO */}
      <section className="relative border-b border-white/10 overflow-hidden">
        <div className="absolute inset-0 grid-lines opacity-50" />
        <div className="absolute inset-0 radial-glow-amber opacity-40" />
        <div className="relative px-6 md:px-12 lg:px-16 pt-24 pb-16">
          <div className="overline text-amber mb-4">
            <span className="inline-flex items-center gap-2">
              <span className="h-px w-6 bg-amber inline-block" />
              Insight · 2-Wheeler OEM intelligence
            </span>
          </div>
          <h1 className="font-display text-[40px] sm:text-5xl md:text-6xl lg:text-7xl text-balance max-w-5xl leading-[1.02]">
            <span className="text-amber">750 dealerships.</span> One customer truth.
          </h1>
          <p className="mt-8 max-w-3xl text-zinc-300 text-base md:text-lg leading-relaxed">
            Picture this: a young EV brand from a storied automotive family,
            two years into the market, already across 750+ dealerships in
            India. Bold ambitions. Beautiful product. And one stubborn problem
            every fast-growing 2-wheeler OEM eventually meets — the customer
            journey lives in 750 silos, filtered through 7,500 salespeople, and
            shows up at headquarters as an aggregated, scrubbed PDF a month
            too late.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link
              to="/contact"
              className="group inline-flex items-center gap-2 border border-amber bg-amber px-6 py-3.5 text-xs font-mono uppercase tracking-[0.18em] text-black transition-colors hover:bg-white hover:border-white"
            >
              Talk to us about this
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <a
              href="#north-star"
              className="inline-flex items-center gap-2 border border-white/25 px-6 py-3.5 text-xs font-mono uppercase tracking-[0.18em] text-white hover:border-white hover:bg-white/5 transition-colors"
            >
              The north star <ArrowRight className="h-3.5 w-3.5" />
            </a>
          </div>
        </div>
      </section>

      {/* NORTH STAR */}
      <section id="north-star" className="px-6 md:px-12 lg:px-16 py-20 md:py-24 bg-black border-b border-white/10">
        <SectionHeader
          kicker="The north star"
          title="Absolute customer truth — captured in real time, across every dealership, without depending on the dealership."
          intro="The OEM should know what its customer felt within minutes of the moment — not in next quarter's NPS deck."
        />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/10">
          {[
            { k: 'Instant', v: 'Feedback inside the moment, not after it.' },
            { k: 'Seamless', v: 'Customer-side WhatsApp + QR. No app to install. No friction.' },
            { k: 'Light', v: 'Under 500 KB on the dealer side. Works on 2G.' },
            { k: 'Independent', v: 'Capture that does not depend on the dealer to type.' },
          ].map((b) => (
            <div key={b.k} className="bg-ink-900 p-7">
              <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-amber mb-3">
                {b.k}
              </div>
              <p className="text-zinc-300 text-sm leading-relaxed">{b.v}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            ['750+', 'Dealerships in scope'],
            ['8', 'Moments of truth tracked'],
            ['<30s', 'Survey completion time'],
            ['T+0', 'Time to insight'],
          ].map(([v, l]) => (
            <div key={l} className="bg-ink-800 border border-white/10 p-5">
              <div className="font-display text-3xl md:text-4xl text-cyan tabular-nums">{v}</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500 mt-2">{l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CHALLENGES */}
      <section className="px-6 md:px-12 lg:px-16 py-20 md:py-24">
        <SectionHeader
          kicker="The reality on the ground"
          title="Eight challenges almost every 2W OEM eventually meets."
          intro="They are uncomfortable. They are universal. And they will not be solved by yet another CRM dashboard."
        />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/10">
          {CHALLENGES.map((c) => (
            <div key={c.n} className="bg-ink-800 p-7 hover:bg-ink-700 transition-colors">
              <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-amber mb-4">/ {c.n}</div>
              <h3 className="font-display text-xl text-white mb-3 leading-snug">{c.t}</h3>
              <p className="text-zinc-400 text-sm leading-relaxed">{c.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SOLUTIONS — the crazy bit */}
      <section className="px-6 md:px-12 lg:px-16 py-20 md:py-28 bg-black border-y border-white/10">
        <SectionHeader
          kicker="Ten ideas worth being crazy about"
          title="Removing the dealership from the dependency path — without removing the dealership."
          intro="A blueprint we'd bring to any 2-wheeler OEM serious about owning the customer relationship at industrial scale. Some of these are live in our platforms today; some are designed for exactly this brief."
        />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {SOLUTIONS.map((s) => {
            const Icon = s.icon;
            return (
              <div
                key={s.n}
                className="group bg-ink-800 border border-white/10 p-7 hover:border-amber/50 transition-colors flex flex-col gap-4 hover-trace"
              >
                <div className="flex items-center justify-between">
                  <span className="grid h-9 w-9 place-items-center bg-amber/10 border border-amber/40">
                    <Icon className="h-4 w-4 text-amber" />
                  </span>
                  <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-amber">/ {s.n}</span>
                </div>
                <h3 className="font-display text-xl text-white leading-snug">{s.t}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed flex-1">{s.d}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* PARTNERSHIP MODEL */}
      <section className="px-6 md:px-12 lg:px-16 py-20 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5">
            <SectionHeader
              kicker="How we'd come in"
              title="As partners — not a vendor on a PO."
              intro="We build, run, and improve this with you. Outcome-linked. No big-bang go-lives. No army of consultants. Just senior operators and a working platform."
            />
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-px bg-white/5 border border-white/10">
            {[
              ['Week 0–6', 'Pilot in ~25 dealerships. WhatsApp + QR + voice listening. Live dashboards in hand.'],
              ['Month 2–4', 'Scale to all 750. Adapters into existing DMS/CRM. Dealer-blind shadow funnel active.'],
              ['Month 4+', 'Connected vehicle telemetry, AI mystery shopper, predictive churn — flywheel running.'],
            ].map(([k, v]) => (
              <div key={k} className="bg-ink-900 p-6">
                <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-amber mb-3">{k}</div>
                <p className="text-zinc-300 text-sm leading-relaxed">{v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="px-6 md:px-12 lg:px-16 py-20 md:py-24 bg-ink-900 border-y border-white/10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="overline text-amber mb-4">
              <span className="inline-flex items-center gap-2">
                <span className="h-px w-6 bg-amber inline-block" />
                FAQ
              </span>
            </div>
            <h2 className="font-display text-3xl md:text-4xl text-balance leading-tight">
              The questions every 2W OEM asks us first.
            </h2>
            <p className="mt-5 text-zinc-400 leading-relaxed">
              Whether you run a young EV-first brand or a 40-year-old
              market leader — these are the conversations we have on the
              first call.
            </p>
          </div>
          <div className="lg:col-span-8">
            <Accordion type="single" collapsible className="border border-white/10 bg-ink-800">
              {FAQS.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`q-${i}`}
                  className="border-b border-white/10 last:border-b-0 px-5"
                >
                  <AccordionTrigger className="font-display text-base md:text-lg text-left text-white hover:no-underline hover:text-amber py-5">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-zinc-400 text-sm leading-relaxed pb-5 pr-6">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative px-6 md:px-12 lg:px-16 py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 radial-glow-amber opacity-50" />
        <div className="relative max-w-4xl">
          <div className="overline text-amber mb-4">
            <span className="inline-flex items-center gap-2">
              <Sparkles className="h-3.5 w-3.5" />
              Built for any 2W OEM serious about owning the customer
            </span>
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-balance leading-[1.02]">
            If 750 dealerships feel like 750 black boxes — let's fix that together.
          </h2>
          <p className="mt-6 text-zinc-300 text-lg max-w-2xl">
            We bring the operators, the technology, the AI &amp; data, and
            where it fits, the capital — to crack this for the entire 2-wheeler
            industry, one OEM at a time.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/contact"
              className="group inline-flex items-center gap-2 border border-amber bg-amber px-7 py-3.5 text-xs font-mono uppercase tracking-[0.18em] text-black transition-colors hover:bg-white hover:border-white"
            >
              Start a conversation
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <Link
              to="/platforms"
              className="inline-flex items-center gap-2 border border-white/25 px-7 py-3.5 text-xs font-mono uppercase tracking-[0.18em] text-white hover:border-white hover:bg-white/5 transition-colors"
            >
              See the platforms behind this <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </section>
    </PageShell>
  );
};

export default Insight2W;
